import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { yupResolver } from "@hookform/resolvers/yup";

import { RequestStatus, Strings } from "@app/constants";
import {
  FormProvider,
  RHFCheckbox,
  RHFDatePicker,
  RHFTextField,
  Spacing,
} from "@app/components";
import { renderDropDown, renderFieldsHeading } from "./render-drop-down";
import { UploadImages } from "./uploadImages";
import ExtraInformationFields from "./extraInfo";
import { addProduct } from "../slice";
import { RootState } from "@app/redux/root-reducer";
import { brand } from "@app/modules/brand/slice";
import { model } from "@app/modules/model/slice";
import { useRequestState } from "@app/hooks";
import { selectAddProductState } from "../selector";
import { PATH_DASHBOARD } from "@app/routes/paths";
import { addNewCarsSchema } from "../vaildations";

export const AddProduct = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const methods = useForm({
    resolver: yupResolver(addNewCarsSchema),
    mode: "onBlur",
    defaultValues: {
      brandId: "",
      modelId: "",
      engineType: "",
      transmission: "",
      driven: "",
      variant: "",
      description: "",
      carCondition: "",
      manufacturingYear: "",
      Price: "" as any,
      color: "",
      insuranceAvailable: "",
      insuranceType: "",
      expiryDate: "",
      anyClaimsMade: "",
      usageType: "",
      rcAvailable: "",
      loanHyonthecation: "",
      accidentHistory: "",
      fullServiceRecord: "",
      isAvailableForSell: false,
      extraInfo: [],
    },
  });

  // Images state
  const [selectedFrontViewImage, setSelectedFrontViewImage] = useState<
    File | string | null
  >(null);
  const [selectedBackViewImage, setSelectedBackViewImage] = useState<
    File | string | null
  >(null);
  const [selectedLeftSideViewImage, setSelectedLeftSideViewImage] = useState<
    File | string | null
  >(null);
  const [selectedRightSideViewImage, setSelectedRightSideViewImage] = useState<
    File | string | null
  >(null);
  const [selectedInteriorDashboardImage, setSelectedInteriorDashboardImage] =
    useState<File | string | null>(null);
  const [selectedOdometerReadingImage, setSelectedOdometerReadingImage] =
    useState<File | string | null>(null);

  const isAvailableForSell = methods.watch("isAvailableForSell");
  const selectedModelId = methods.watch("modelId");
  const selectedBrandId = methods.watch("brandId");

  // Redux state
  const { brandData, brandsLoading, modelData, modelsLoading } = useSelector(
    (state: RootState) => {
      const brandState = state.brand.brand;
      const modelState = state.modelReducer.model;
      return {
        brandData: brandState.data,
        brandsLoading: brandState.status === RequestStatus.RUNNING,
        modelData: modelState.data,
        modelsLoading: modelState.status === RequestStatus.RUNNING,
      };
    },
  );

  const selectedBrand = brandData?.find(
    (b: any) => b.id === Number(selectedBrandId),
  );

  const modelsForBrand = selectedBrand?.models || [];

  const handleNavigate = useCallback(() => {
    navigate(PATH_DASHBOARD.product);
  }, []);

  useRequestState({
    stateSelector: selectAddProductState,
    successMessageShown: true,
    errorShown: true,
    onSuccess: handleNavigate,
  });

  useEffect(() => {
    dispatch(brand());
    dispatch(model());
  }, [dispatch]);

  const selectedModel = modelData?.find(
    (m: any) => m.id === Number(selectedModelId),
  );
  // const variantsForModel = selectedModel?.variant || [];
  const variantsForModel =
    selectedModel?.variant?.[0]?.split(",").map((v: string) => v.trim()) || [];

  useEffect(() => {
    if (variantsForModel.length === 1) {
      methods.setValue("variant", variantsForModel[0]);
    } else {
      methods.setValue("variant", "");
    }
  }, [selectedModelId]);

  const onSubmit = (form: any) => {
    try {
      const formData = new FormData();

      const keyMap: Record<string, string> = {
        loanHyonthecation: "loanHypothecation",
        fullServiceRecord: "serviceHistory",
        isAvailableForSell: "carAvailableForSell",
        Price: "price",
        expiryDate: "insuranceValidTill",
        extraInfo: "extraInformation",
      };

      Object.keys(form).forEach((key) => {
        const backendKey = keyMap[key] || key;
        let value = form[key];

        // Convert Yes/No to boolean
        if (
          ["insuranceAvailable", "anyClaimsMade", "rcAvailable"].includes(
            backendKey,
          )
        ) {
          value = value === "Yes" || value === true ? true : false;
        }

        if (backendKey === "carAvailableForSell") value = !!value;

        if (backendKey === "insuranceValidTill" && value) {
          value = dayjs(value).format("YYYY-MM-DD");
        }

        if (typeof value === "number") value = value.toString();
        
        // Convert string price to number before appending
        if (backendKey === "price" && typeof value === "string") {
          value = Number(value);
        }

        if (backendKey === "extraInformation" && Array.isArray(value)) {
          value.forEach((item: any, index: number) => {
            formData.append(
              `extraInformation[${index}][heading]`,
              item.heading || "",
            );
            formData.append(
              `extraInformation[${index}][description]`,
              item.description || "",
            );
          });
          return;
        }

        if (value !== undefined && value !== null && key !== "extraInfo")
          formData.append(backendKey, value);
      });

      // Append images
      const images = [
        selectedFrontViewImage,
        selectedBackViewImage,
        selectedLeftSideViewImage,
        selectedRightSideViewImage,
        selectedInteriorDashboardImage,
        selectedOdometerReadingImage,
      ].filter((img): img is File | string => img !== null && img !== undefined);

      if (images.length > 0) {
        images.forEach((img) => formData.append("images", img));
      }

      console.log("Submitting form data:", formData);
      dispatch(addProduct({ formValues: formData as any }));
    } catch (error) {
      console.error("Add Product failed:", error);
    }
  };

  return (
    <Stack
      sx={{
        px: 2,
        pt: 0,
        pb: 5,
        bgcolor: theme.palette.common.white,
        borderRadius: 2,
      }}
    >
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        {renderFieldsHeading(Strings.product.carDetails)}

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.brand, brandData, brandsLoading)}
          {renderDropDown(Strings.product.manufacturingYear)}
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.model, modelsForBrand, modelsLoading)}
          {renderDropDown(Strings.product.engineType)}
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.transmissionType)}
          {renderDropDown(
            Strings.product.variant,
            variantsForModel.map((v: string, i: number) => ({
              id: i,
              name: v,
            })),
          )}
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.carTraveled)}
          <Stack
            sx={{
              width: { xs: "100%", md: "48%" },
              mb: 2,
              mt: { xs: 2, md: 0 },
            }}
          >
            <Typography
              sx={{
                mb: 1,
                fontWeight: 600,
                fontFamily: "Work Sans",
                color: "#222",
              }}
            >
              Price
            </Typography>
            <RHFTextField name="Price" placeholder="Add Price" type="number" />
          </Stack>
        </Stack>
        <Stack
          sx={{ width: { xs: "100%", md: "48%" }, mb: 2, mt: { xs: 2, md: 0 } }}
        >
          <Typography
            sx={{
              mb: 1,
              fontWeight: 600,
              fontFamily: "Work Sans",
              color: "#222",
            }}
          >
            Color
          </Typography>
          <RHFTextField name="color" placeholder="Add Color" />
        </Stack>
        <Stack sx={{ width: "100%", mb: 2, mt: 2 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontFamily: "Work Sans",
              color: "#222",
              mb: 1,
            }}
          >
            Description
          </Typography>
          <RHFTextField
            name="description"
            placeholder="Add description..."
            multiline
            rows={3}
          />
        </Stack>

        {renderFieldsHeading(Strings.product.insurance)}
        <Stack
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.insuranceAvailable)}
          {renderDropDown(Strings.product.insuranceType)}
        </Stack>

        <Stack
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          <Stack
            sx={{
              width: { xs: "100%", md: "48%" },
              mb: 2,
              mt: { xs: 2, md: 0 },
            }}
          >
            <Typography
              sx={{
                mb: 1,
                fontWeight: 600,
                fontFamily: "Work Sans",
                color: "#222",
              }}
            >
              {Strings.product.expiryDate}
            </Typography>
            <RHFDatePicker name="expiryDate" label="Expiry Date" />
          </Stack>
          {renderDropDown(Strings.product.anyClaimsMade)}
        </Stack>

        {renderFieldsHeading(Strings.product.ownershipAndRC)}
        <Stack
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.usageType)}
          {renderDropDown(Strings.product.rcAvailable)}
        </Stack>
        {renderDropDown(Strings.product.loanHyonthecation)}

        {renderFieldsHeading(Strings.product.accidentAndServiceHistory)}
        <Stack
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.accidentHistory)}
          {renderDropDown(Strings.product.fullServiceRecord)}
        </Stack>

        {renderFieldsHeading(Strings.product.carCondition)}
        {renderDropDown(Strings.product.carCondition)}

        <RHFCheckbox
          label={Strings.product.isAvailableForSell || "Is Available For Sell"}
          name="isAvailableForSell"
        />

        <ExtraInformationFields label="Extra Information" />

        {renderFieldsHeading(Strings.product.productImage)}
        <UploadImages
          selectedFrontViewImage={selectedFrontViewImage}
          setSelectedFrontViewImage={setSelectedFrontViewImage}
          selectedBackViewImage={selectedBackViewImage}
          setSelectedBackViewImage={setSelectedBackViewImage}
          selectedLeftSideViewImage={selectedLeftSideViewImage}
          setSelectedLeftSideViewImage={setSelectedLeftSideViewImage}
          selectedRightSideViewImage={selectedRightSideViewImage}
          setSelectedRightSideViewImage={setSelectedRightSideViewImage}
          selectedInteriorDashboardImage={selectedInteriorDashboardImage}
          setSelectedInteriorDashboardImage={setSelectedInteriorDashboardImage}
          selectedOdometerReadingImage={selectedOdometerReadingImage}
          setSelectedOdometerReadingImage={setSelectedOdometerReadingImage}
        />

        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "end",
            gap: 2,
            mt: 4,
          }}
        >
          <Button
            sx={{ width: { xs: "40%", sm: 250 } }}
            variant="outlined"
            onClick={() => navigate(-1)}
          >
            {Strings.button.cancel}
          </Button>
          <Button
            sx={{
              width: { xs: "40%", sm: 250 },
              color: theme.palette.common.white,
            }}
            type="submit"
            variant="contained"
            disabled={methods.formState.isSubmitting}
          >
            {methods.formState.isSubmitting ? "Saving..." : Strings.button.save}
          </Button>
        </Stack>
      </FormProvider>
    </Stack>
  );
};
